import {useState} from 'react';
import {useSetRecoilState} from 'recoil';

import {logEventBooking} from 'main/analytics/analytics';
import {LogEventBookingEnum} from 'main/analytics/logEventEnum';
import {
	makePostExamsRebook,
	makePostOdontoRebook,
	makePutHealthRescheduleAppointments,
} from 'main/factories/usecases';
import {
	FacialBiometricEnum,
	FeaturesEnum,
	OperationStateType,
	OperationStateTypesEnum,
} from 'presentation/constants';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {refresherState} from 'presentation/pages/states';
import extractTimeFromUTC from 'presentation/utils/extractTimeFromUTC';
import {
	BookingTypeEnum,
	CONFIRMATION_PATH,
	EntryPointCodeEnum,
	FlowEnum,
	TELEMEDICINE_INFO,
} from '../../constants';

import {HandleConfirmationParams, UseRebookingProps} from './types';
import {resolveBookingError} from 'presentation/modules/booking/shared/utils/resolveBookingError';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';
import {useFacialBiometricFeatureFlag} from 'presentation/pages/private/hooks/useFacialBiometricFeatureFlag';
import {useLocation, useNavigate} from 'react-router-dom';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

export function useRebooking({flowType}: UseRebookingProps) {
	const navigate = useNavigate();
	const {search} = useLocation();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {companyPortal} = useAccountDataStore(state => state.accountData);

	const hasFacialBiometrics = useFacialBiometricsStore(state => state.hasFacialBiometrics);
	const registerLater = useFacialBiometricsStore(state => state.registerLater);
	const {isEnabled: isFacialBiometricEnabled} = useFacialBiometricFeatureFlag();

	const [rebookingState, setRebookingState] = useState<OperationStateType | null>(null);

	const {refetch} = useGetHomeQuery();

	const setRefresher = useSetRecoilState(refresherState);

	const {isEnabled: isHideConfirmationBookingModalEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_HIDE_CONFIRMATION_BOOKING_MODAL,
	);

	const isTelehealth = flowType === FlowEnum.TELEHEALTH;

	async function handleConfirmOperation({
		bookingType,
		id,
		protocolNumber,
	}: HandleConfirmationParams) {
		setBookingData(prev => ({
			...prev,
			protocolNumber,
			bookingType,
			bookingNumber: id,
		}));

		await refetch();
		setRefresher(value => value + 1);

		if (isHideConfirmationBookingModalEnabled) {
			const path = CONFIRMATION_PATH[bookingType].replace(':id', id);

			navigate(path);
			setRebookingState(null);
		} else {
			setRebookingState(OperationStateTypesEnum.DONE);
		}
	}

	async function rebookHealth(bookingNumber: string) {
		if (isTelehealth && !hasFacialBiometrics && isFacialBiometricEnabled && !registerLater) {
			navigate({pathname: FacialBiometricEnum.FACIAL_BIOMETRIC, search});
			return;
		}

		const formattedHour = extractTimeFromUTC(bookingData.hourAt);
		const formattedDate = `${bookingData.dateAt} ${formattedHour}`;

		try {
			setRebookingState(OperationStateTypesEnum.SUBMITTING);
			const {content} = await makePutHealthRescheduleAppointments().put({
				specialtyCode: Number(bookingData.specialtyCode),
				subSpecialtyCode: bookingData.subSpecialtyCode
					? bookingData.subSpecialtyCode
					: undefined,
				providerCode: Number(bookingData.professionalCode),
				clinicCode: isTelehealth
					? Number(TELEMEDICINE_INFO.code)
					: Number(bookingData.unitCode),
				date: formattedDate,
				entryPointCode: Number(EntryPointCodeEnum.V2),
				number: Number(bookingNumber),
				email: bookingData.email,
				cellPhoneNumber: bookingData.phone,
				operatorName: companyPortal?.operatorName,
				operatorEmail: companyPortal?.operatorEmail,
				operatorDocument: companyPortal?.operatorDocument,
			});

			logEventBooking({
				eventName: isTelehealth
					? LogEventBookingEnum.PORTAL_TELE_BOOKING_COMPLETE
					: LogEventBookingEnum.PORTAL_HEALTH_BOOKING_COMPLETE,
			});

			handleConfirmOperation({
				bookingType: isTelehealth ? BookingTypeEnum.TELEHEALTH : BookingTypeEnum.HEALTH,
				id: content.number.toString(),
				protocolNumber: content.protocol.toString(),
			});
		} catch (error) {
			const {state} = resolveBookingError(error);
			setRebookingState(state);
		}
	}

	async function rebookOdonto(bookingNumber: string) {
		try {
			setRebookingState(OperationStateTypesEnum.SUBMITTING);
			const {reagendamento} = await makePostOdontoRebook().post({
				cdEspecialidade: Number(bookingData.specialtyCode),
				cdSubEspecialidade: bookingData.subSpecialtyCode
					? Number(bookingData.subSpecialtyCode)
					: 0,
				cdPrestadorFisico: bookingData.professionalCode,
				cdPrestadorJuridico: bookingData.unitCode,
				dtConsulta: bookingData.dateAt,
				horarioConsulta: bookingData.hourAt,
				cdPontoEntrada: EntryPointCodeEnum.V1,
				nuConsulta: bookingNumber,
			});

			logEventBooking({eventName: LogEventBookingEnum.PORTAL_DENTAL_BOOKING_COMPLETE});

			handleConfirmOperation({
				bookingType: BookingTypeEnum.ODONTO,
				id: reagendamento.numero.toString(),
				protocolNumber: reagendamento.nuProtocolo.toString(),
			});
		} catch (error) {
			const {state} = resolveBookingError(error);
			setRebookingState(state);
		}
	}

	async function rebookExam(bookingNumber: string) {
		try {
			setRebookingState(OperationStateTypesEnum.SUBMITTING);
			const {reagendamentoExame} = await makePostExamsRebook().post({
				cdTipoExame: Number(bookingData.specialtyCode),
				cdPrestadorFisico: bookingData.professionalCode,
				cdPrestadorJuridico: bookingData.unitCode,
				dtAgendamento: bookingData.dateAt,
				horarioAgendamento: bookingData.hourAt,
				cdPontoEntrada: EntryPointCodeEnum.V1,
				nuExame: bookingNumber,
				email: bookingData.email,
				nuCelular: bookingData.phone,
				nmOperador: companyPortal?.operatorName,
				emailOperador: companyPortal?.operatorEmail,
				documentoOperador: companyPortal?.operatorDocument,
			});

			logEventBooking({eventName: LogEventBookingEnum.PORTAL_EXAM_BOOKING_COMPLETE});

			handleConfirmOperation({
				bookingType: BookingTypeEnum.EXAM,
				id: reagendamentoExame.nuExameRemarcado.toString(),
				protocolNumber: reagendamentoExame.protocoloAgendamento.toString(),
			});
		} catch (error) {
			const {state} = resolveBookingError(error);
			setRebookingState(state);
		}
	}

	return {
		rebookHealth,
		rebookExam,
		rebookOdonto,
		rebookingState,
		setRebookingState,
	};
}
